import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { useOCPStore } from '../hooks/useOCPStore';
import { useDraftFlowStore } from '../hooks/useDraftFlowStore';
import PanelEntry from './PanelEntry';
import {
  Button,
  Icon,
  TextArea,
  TextField,
  Typography,
  SelectMenu,
  Switch,
  BaseCheckbox,
  InputLabel,
} from '@scale/scaleui';
import {
  OnboardingFlowNode,
  OnboardingStepType,
  OnboardingFlowStepNode,
  OnboardingFlowTerminusNode,
  OnboardingFlowNodeType,
  OnboardingFlowTransition,
  ConditionType,
  ConditionValue,
  ConditionValueType,
  UserPropertyCondition,
  StepOutputCondition,
  ConditionUserProperty,
  CourseStepConfig,
  AwaitCourseOutcomeStepConfig,
  PlatformCourseStepConfig,
  TransitionCondition,
  CONDITION_USER_PROPERTIES,
  isDefaultFallback,
  FlowTerminusDestinationType,
  ScheduleInterviewStepConfig,
  InterviewOutcomeStepConfig,
  AsyncInterviewStepConfig,
  SkillScreeningStepConfig,
  ResumeScreenStepConfig,
  WorkerSkillCondition,
  JoinCommunityStepConfig,
  ResumeMetadataCondition,
  UTMCondition,
  OnboardingFlowNodeProjectDistribution,
  OnboardingStepAttributeKey,
  OnboardingFlowType,
  getOnboardingStepAttributeMap,
  DEPRECATED_OCP_STEPS,
} from '@scale/shared/onboardingControlPanel';
import {
  UNCONNECTABLE_STEP_TYPES_NAMES,
  isNodeUneditable,
  isTransitionUneditable,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/config/config';
import { useBypassWhitelistStore } from '../hooks/useBypassWhitelistStore';
import ConditionValueInput from './conditionValueInput/ConditionValueInput';
import CourseAutocomplete from '../../components/CourseAutocomplete';
import WorkerTeamAutocomplete from './WorkerTeamAutocomplete';
import OnboardingFlowAutocomplete from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/OnboardingFlowAutocomplete';
import ResumeScreeningConfigAutocomplete from './ResumeScreeningConfigAutocomplete';
import CopyToClipboard from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { getFlowById } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { InterviewStatus } from '@scale/shared/interview';
import {
  WorkerSkillDimension,
  WorkerSkillDimensionQualityCategory,
  ResumeScreeningResult,
  ResumeMetadataFields,
  WorkerSource,
  UTMParameters,
} from '@scale/shared-types';
import WorkerSkillConditionValueInput, {
  WorkerSkillConditionValue,
} from '../../components/WorkerSkillInput';
import { CommunityType, CourseVersion } from '@scale/shared/defaults';
import ResumeMetadataConditionValueInput from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/conditionValueInput/ResumeMetadataConditionValueInput';
import { LoadBalancerModal } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/LoadBalancerModal';
import {
  TERMINUS_FIELDS,
  STEP_FIELDS,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/common';
import UTMConditionValueInput from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/conditionValueInput/UTMConditionValueInput';
import { MARKETPLACE_WORKER_TEAM_ID } from '@scale/shared/outlier/constants';
import { MAX_WEIGHT, ProjectDistributionConfig } from './ProjectDistributionConfig';

export const notifyCopySuccess = () =>
  toast('Copied!', {
    position: 'bottom-center',
    autoClose: 1000,
    progressStyle: { background: 'green' },
  });

interface NameInputProps {
  loadingStepStats?: boolean;
  activeNode?: boolean; // users have passed through before
}

export const NameInput: React.FC<NameInputProps> = ({ loadingStepStats, activeNode }) => {
  const { currentFlow, detailTarget, setDetailTarget, currentVersion, setCurrentVersion } =
    useOCPStore(state => ({
      currentFlow: state.currentFlow,
      detailTarget: state.detailTarget as OnboardingFlowNode,
      setDetailTarget: state.setDetailTarget,
      currentVersion: state.currentVersion,
      setCurrentVersion: state.setCurrentVersion,
    }));
  const { editName, draftFlows } = useDraftFlowStore(state => ({
    editName: state.editDraftEntityName,
    draftFlows: state.draftFlows,
  }));

  const [nameEditMode, setNameEditMode] = useState<boolean>(false);
  const [name, setName] = useState<string>(detailTarget?.name ?? '');
  const uneditable =
    currentVersion !== -1 || loadingStepStats || (currentFlow?.isPublished && activeNode);

  useEffect(() => {
    if (detailTarget?.name) setName(detailTarget.name);
  }, [detailTarget?.name]);

  const handleStartEdit = () => {
    setNameEditMode(true);
    if (currentVersion !== -1) setCurrentVersion(-1);
  };
  const confirmEditName = () => {
    setNameEditMode(false);
    if (currentFlow) {
      const draftFlow = draftFlows[currentFlow._id];
      if (draftFlow && detailTarget && detailTarget.name !== name) {
        // node name cannot exist in the flow
        if (draftFlow.nodes.find(n => n.name === name)) {
          toast.error('Name is the same as a node name');
          setName(detailTarget.name);
        } else if (draftFlow.edges.find(edge => edge.name === name)) {
          toast.error('Name is the same as an edge name');
          setName(detailTarget.name);
        } else {
          const newNode = editName(currentFlow._id, detailTarget.name, name);
          setDetailTarget(newNode);
        }
      }
    }
  };

  return (
    <div className="flex gap-1 items-center p-2 w-full">
      {nameEditMode && detailTarget?.name ? (
        <TextField
          id="node-name"
          className="w-80"
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              confirmEditName();
            }
          }}
        />
      ) : (
        <Typography.H4 className="truncate">{detailTarget?.name ?? ''}</Typography.H4>
      )}
      {!uneditable && !nameEditMode && detailTarget?.name && (
        <Icon
          rounded
          size="sm"
          variant="neutral"
          icon="pencil"
          onClick={handleStartEdit}
          className="shrink-0"
        />
      )}
      {nameEditMode && (
        <Icon rounded size="sm" variant="neutral" icon="check" onClick={() => confirmEditName()} />
      )}
      {uneditable && (
        <Icon
          rounded
          size="sm"
          variant="neutral"
          icon="lock"
          className="shrink-0"
          tooltip="node name cannot be edited once flow is published and users have passed through"
        />
      )}
    </div>
  );
};

export const ContributorFacingNameInput: React.FC = () => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const editDraftEntityValue = useDraftFlowStore(state => state.editDraftEntityValue);
  const [externalName, setExternalName] = useState<string>(
    (detailTarget as OnboardingFlowNode)?.externalName ?? '',
  );
  const uneditable =
    currentVersion !== -1 ||
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null);

  const handleEdit = () => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        externalName,
      });
      setDetailTarget(newDetailTarget);
    }
  };

  useEffect(() => {
    setExternalName((detailTarget as OnboardingFlowNode)?.externalName ?? '');
  }, [detailTarget]);

  return (
    <PanelEntry title="Contributor-Facing Name">
      <TextField
        id="contributor-facing-name"
        placeholder="Name..."
        value={externalName}
        onChange={e => setExternalName(e.target.value)}
        onBlur={handleEdit}
        disabled={uneditable}
      />
    </PanelEntry>
  );
};

export const ContributorFacingDescriptionInput: React.FC = () => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const editDraftEntityValue = useDraftFlowStore(state => state.editDraftEntityValue);
  const [externalDescription, setExternalDescription] = useState<string>(
    (detailTarget as OnboardingFlowNode)?.externalDescription ?? '',
  );
  const uneditable =
    currentVersion !== -1 ||
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null);

  const handleEdit = () => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        externalDescription,
      });
      setDetailTarget(newDetailTarget);
    }
  };

  useEffect(() => {
    setExternalDescription((detailTarget as OnboardingFlowNode)?.externalDescription ?? '');
  }, [detailTarget]);

  return (
    <PanelEntry title="Contributor-Facing Description">
      <TextField
        id="contributor-facing-description"
        placeholder="Description..."
        value={externalDescription}
        onChange={e => setExternalDescription(e.target.value)}
        onBlur={handleEdit}
        disabled={uneditable}
      />
    </PanelEntry>
  );
};

export const TerminusDestinationInput: React.FC<{ required?: boolean }> = ({ required }) => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const editDraftEntityValue = useDraftFlowStore(state => state.editDraftEntityValue);
  const [destinationTeam, setDestinationTeam] = useState<{ id: string; title: string }>({
    id: (detailTarget as OnboardingFlowTerminusNode)?.destinationId ?? '',
    title: '',
  });
  const [destinationFlow, setDestinationFlow] = useState<{ id: string; title: string }>({
    id: (detailTarget as OnboardingFlowTerminusNode)?.destinationId ?? '',
    title: '',
  });
  const [destinationId, setDestinationId] = useState<string>(
    (detailTarget as OnboardingFlowTerminusNode)?.destinationId ?? '',
  );
  const [routeToFlow, setRouteToFlow] = useState<boolean>(
    (detailTarget as OnboardingFlowTerminusNode)?.destinationType ===
      FlowTerminusDestinationType.Flow,
  );
  const [destinationTitle, setDestinationTitle] = useState<string>('');
  const [chosenProjectDistribution, setChosenProjectDistribution] = useState<
    OnboardingFlowNodeProjectDistribution[]
  >(
    (detailTarget as OnboardingFlowTerminusNode)?.chosenProjectId
      ? [
          {
            projectId: (detailTarget as OnboardingFlowTerminusNode)?.chosenProjectId || '',
            distribution: MAX_WEIGHT,
          },
        ]
      : (detailTarget as OnboardingFlowTerminusNode)?.chosenProjectDistribution ?? [],
  );

  const uneditable =
    currentVersion !== -1 ||
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null);

  useEffect(() => {
    if (routeToFlow) {
      setDestinationId((detailTarget as OnboardingFlowTerminusNode)?.destinationId ?? '');
      setDestinationFlow({
        id: (detailTarget as OnboardingFlowTerminusNode)?.destinationId ?? '',
        title: '',
      });
    } else {
      setDestinationId((detailTarget as OnboardingFlowTerminusNode)?.destinationId ?? '');
      setDestinationTeam({
        id: (detailTarget as OnboardingFlowTerminusNode)?.destinationId ?? '',
        title: '',
      });
    }
  }, [detailTarget, routeToFlow]);

  useEffect(() => {
    if (routeToFlow) {
      if (currentFlow && detailTarget && destinationFlow.title) {
        const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
          ...detailTarget,
          destinationId: destinationFlow.id,
          destinationType: FlowTerminusDestinationType.Flow,
        });
        setDestinationId(destinationFlow.id);
        setDetailTarget(newDetailTarget);
      }
    } else if (currentFlow && detailTarget && destinationTeam.title) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        destinationId: destinationTeam.id,
        destinationType: FlowTerminusDestinationType.WorkerTeam,
      });
      setDestinationId(destinationTeam.id);
      setDetailTarget(newDetailTarget);
    }
  }, [
    destinationTeam,
    destinationFlow,
    currentFlow,
    editDraftEntityValue,
    setDetailTarget,
    routeToFlow,
  ]);

  // Set the destination title based on the destination type
  useEffect(() => {
    if (currentFlow && detailTarget) {
      if (routeToFlow && destinationFlow.title) setDestinationTitle(destinationFlow.title);
      else if (routeToFlow && destinationId) {
        (async () => {
          try {
            const flow = await getFlowById(destinationId);
            if (flow?.name) setDestinationTitle(flow.name);
            // eslint-disable-next-line no-empty
          } catch {}
        })();
      } else if (destinationTeam.title) setDestinationTitle(destinationTeam.title);
      else if (destinationId) {
        (async () => {
          try {
            const team = await apiFetch(null, `corp-api/worker_teams/team/${destinationId}`);
            if (team?.name) setDestinationTitle(team.name);
            // eslint-disable-next-line no-empty
          } catch {}
        })();
      }
    }
  }, [currentFlow, destinationFlow, destinationId, destinationTeam, detailTarget, routeToFlow]);

  useEffect(() => {
    if (
      !routeToFlow &&
      currentFlow &&
      detailTarget &&
      destinationId === MARKETPLACE_WORKER_TEAM_ID &&
      !_.isEqual(terminusNode?.chosenProjectDistribution, chosenProjectDistribution)
    ) {
      // Only update when we have a project ID and it's a user-initiated change
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget, // Keep all existing properties
        destinationType: FlowTerminusDestinationType.WorkerTeam,
        destinationId,
        chosenProjectDistribution,
      });

      if (newDetailTarget) {
        setDetailTarget(newDetailTarget);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenProjectDistribution]);

  const terminusNode = useMemo(() => {
    return detailTarget &&
      'type' in detailTarget &&
      detailTarget.type === OnboardingFlowNodeType.Terminus
      ? (detailTarget as OnboardingFlowTerminusNode)
      : null;
  }, [detailTarget]);

  useEffect(() => {
    if (
      terminusNode &&
      !_.isEqual(terminusNode.chosenProjectDistribution, chosenProjectDistribution)
    ) {
      setChosenProjectDistribution(
        !terminusNode.chosenProjectDistribution?.length && terminusNode.chosenProjectId
          ? [
              {
                projectId: terminusNode.chosenProjectId,
                distribution: MAX_WEIGHT,
              },
            ]
          : terminusNode.chosenProjectDistribution ?? [],
      );
    }
  }, [terminusNode?.chosenProjectDistribution]);

  return (
    <div>
      <PanelEntry title={''}>
        <div className="flex flex-row gap-2 mb-2 items-center">
          <BaseCheckbox
            checked={routeToFlow}
            className=""
            onClick={() => setRouteToFlow(!routeToFlow)}
            size="md"
            variant="primary"
            disabled={uneditable}
          />
          <InputLabel
            className="text-gray-500"
            labelDescription="If checked, contributors will be routed to a different flow upon completion of this step. If unchecked, they will be routed to a worker team."
            size="md"
            tooltipContentClassName="text-gray-500"
          >
            Route to Onboarding Flow
          </InputLabel>
        </div>
      </PanelEntry>
      {!routeToFlow && (
        <PanelEntry title={`Destination WorkerTeam ID${required ? '*' : ``}`}>
          {!!destinationTitle.length && (
            <Typography.Paragraph3 className="ml-1">
              WorkerTeam Name: {destinationTitle}
            </Typography.Paragraph3>
          )}
          <div className="flex gap-1 items-center">
            <Typography.Paragraph3 className="ml-1">
              WorkerTeam ID: {destinationId}
            </Typography.Paragraph3>
            {destinationId && (
              <CopyToClipboard text={destinationId} onCopy={notifyCopySuccess}>
                <Icon icon="copy" variant="neutral" size="sm" />
              </CopyToClipboard>
            )}
          </div>
          <WorkerTeamAutocomplete
            selectedWorkerTeam={destinationTeam}
            setSelectedWorkerTeam={setDestinationTeam}
            disabled={uneditable}
          />
        </PanelEntry>
      )}
      {!routeToFlow && destinationId === MARKETPLACE_WORKER_TEAM_ID && (
        <ProjectDistributionConfig
          chosenProjectDistribution={chosenProjectDistribution}
          setChosenProjectDistribution={setChosenProjectDistribution}
        />
      )}
      {routeToFlow && (
        <PanelEntry title={`Destination Flow ID${required ? '*' : ``}`}>
          {!!destinationTitle.length && (
            <Typography.Paragraph3 className="ml-1">
              Flow Name: {destinationTitle}
            </Typography.Paragraph3>
          )}
          <div className="flex gap-1 items-center">
            <Typography.Paragraph3 className="ml-1">Flow ID: {destinationId}</Typography.Paragraph3>
            {destinationId && (
              <CopyToClipboard text={destinationId} onCopy={notifyCopySuccess}>
                <Icon icon="copy" variant="neutral" size="sm" />
              </CopyToClipboard>
            )}
          </div>
          <OnboardingFlowAutocomplete
            selectedFlow={destinationFlow}
            setSelectedFlow={setDestinationFlow}
            disabled={uneditable}
          />
        </PanelEntry>
      )}
      <SuccessfulTerminusNodeToggle routeToFlow={routeToFlow} />,
    </div>
  );
};

export const FinalizeConditionInput: React.FC<{ required: boolean }> = ({ required }) => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const editDraftEntityValue = useDraftFlowStore(state => state.editDraftEntityValue);
  const [finalizeCondition, setFinalizeCondition] = useState<
    | InterviewStatus.Completed
    | InterviewStatus.Scheduled
    | InterviewStatus.ReviewRequired
    | undefined
  >(
    (
      (detailTarget as OnboardingFlowStepNode).config as
        | ScheduleInterviewStepConfig
        | AsyncInterviewStepConfig
    )?.finalizeCondition,
  );
  const uneditable =
    currentVersion !== -1 ||
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null);

  const handleEdit = () => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        config: {
          ...((detailTarget as OnboardingFlowStepNode).config ?? {}),
          finalizeCondition,
        },
      });
      setDetailTarget(newDetailTarget);
    }
  };

  let optionsList: { id: InterviewStatus; name: string }[];
  if ((detailTarget as OnboardingFlowStepNode).stepType === OnboardingStepType.ScheduleInterview) {
    optionsList = [
      { id: InterviewStatus.Scheduled, name: 'Scheduled' },
      { id: InterviewStatus.Completed, name: 'Completed' },
    ];
  } else {
    optionsList = [
      { id: InterviewStatus.ReviewRequired, name: 'Submitted' },
      { id: InterviewStatus.Completed, name: 'Reviewed' },
    ];
  }

  useEffect(() => {
    const condition = (
      (detailTarget as OnboardingFlowStepNode).config as
        | ScheduleInterviewStepConfig
        | AsyncInterviewStepConfig
    )?.finalizeCondition;

    if (optionsList.map(o => o.id).includes(condition)) {
      setFinalizeCondition(condition);
    } else {
      setFinalizeCondition(undefined);
    }
  }, [detailTarget]);

  return (
    <PanelEntry title={`Advance CB when interview is${required ? '*' : ''}`}>
      <SelectMenu
        className="w-48"
        label=""
        menuDirection="bottom"
        onBlur={handleEdit}
        options={optionsList}
        selectedOptionId={finalizeCondition!}
        setSelected={v =>
          setFinalizeCondition(
            v as
              | InterviewStatus.Scheduled
              | InterviewStatus.Completed
              | InterviewStatus.ReviewRequired,
          )
        }
        disabled={uneditable}
        required={true}
      />
    </PanelEntry>
  );
};

export const CourseIdInput: React.FC<{ required?: boolean; stepType: OnboardingStepType }> = ({
  required,
  stepType,
}) => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const editDraftEntityValue = useDraftFlowStore(state => state.editDraftEntityValue);
  const [course, setCourse] = useState<{
    id: string;
    title: string;
    courseVersion?: CourseVersion;
  }>({
    id: ((detailTarget as OnboardingFlowStepNode)?.config as CourseStepConfig)?.courseId ?? '',
    title:
      ((detailTarget as OnboardingFlowStepNode)?.config as CourseStepConfig)?.courseTitle ?? '',
    courseVersion:
      ((detailTarget as OnboardingFlowStepNode)?.config as CourseStepConfig)?.courseVersion ??
      CourseVersion.V1,
  });
  const uneditable =
    currentVersion !== -1 ||
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null);

  useEffect(() => {
    setCourse({
      id:
        (
          (detailTarget as OnboardingFlowStepNode)?.config as
            | CourseStepConfig
            | AwaitCourseOutcomeStepConfig
            | PlatformCourseStepConfig
        )?.courseId ?? '',
      title:
        (
          (detailTarget as OnboardingFlowStepNode)?.config as
            | CourseStepConfig
            | AwaitCourseOutcomeStepConfig
            | PlatformCourseStepConfig
        )?.courseTitle ?? '',
      courseVersion:
        (
          (detailTarget as OnboardingFlowStepNode)?.config as
            | CourseStepConfig
            | AwaitCourseOutcomeStepConfig
            | PlatformCourseStepConfig
        )?.courseVersion ?? CourseVersion.V1,
    });
  }, [detailTarget?.name]);

  useEffect(() => {
    if (currentFlow && detailTarget && course.title) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        config: {
          ...((detailTarget as OnboardingFlowStepNode).config ?? {}),
          type: stepType,
          courseId: course.id,
          courseTitle: course.title,
          courseVersion: course.courseVersion,
        },
      });
      setDetailTarget(newDetailTarget);
    }
  }, [course, currentFlow, editDraftEntityValue, setDetailTarget, stepType]);

  return (
    <PanelEntry title={`Course${required ? '*' : ``}`}>
      {!!course.title.length && (
        <Typography.Paragraph3 className="ml-1">Course Name: {course.title}</Typography.Paragraph3>
      )}
      <div className="flex gap-1 items-center">
        <Typography.Paragraph3 className="ml-1">Course ID: {course.id}</Typography.Paragraph3>
        {course.id && (
          <CopyToClipboard text={course.id} onCopy={notifyCopySuccess}>
            <Icon icon="copy" variant="neutral" size="sm" />
          </CopyToClipboard>
        )}
      </div>

      <CourseAutocomplete
        selectedCourse={course}
        setSelectedCourse={setCourse}
        disabled={uneditable}
      />
    </PanelEntry>
  );
};

export const ResumeScreeningConfigIdInput: React.FC<{
  required?: boolean;
  stepType: OnboardingStepType;
}> = ({ required, stepType }) => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const detailTargetRef = useRef(detailTarget);
  const editDraftEntityValue = useDraftFlowStore(state => state.editDraftEntityValue);
  const [resumeScreeningConfig, setResumeScreeningConfig] = useState<{
    id: string;
    title: string;
    nodeOnly?: boolean;
  }>({
    id:
      ((detailTarget as OnboardingFlowStepNode)?.config as ResumeScreenStepConfig)
        ?.resumeScreeningConfigId ?? '',
    title:
      ((detailTarget as OnboardingFlowStepNode)?.config as ResumeScreenStepConfig)
        ?.resumeScreeningConfigTitle ?? '',
    nodeOnly:
      ((detailTarget as OnboardingFlowStepNode)?.config as ResumeScreenStepConfig)
        ?.resumeScreeningConfigNodeOnly ?? false,
  });

  const uneditable =
    currentVersion !== -1 ||
    (detailTarget?.name !== 'Resume Screen' &&
      isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null));

  useEffect(() => {
    const newId =
      ((detailTarget as OnboardingFlowStepNode)?.config as ResumeScreenStepConfig)
        ?.resumeScreeningConfigId ?? '';
    const newTitle =
      ((detailTarget as OnboardingFlowStepNode)?.config as ResumeScreenStepConfig)
        ?.resumeScreeningConfigTitle ?? '';
    const newNodeOnly =
      ((detailTarget as OnboardingFlowStepNode)?.config as ResumeScreenStepConfig)
        ?.resumeScreeningConfigNodeOnly ?? false;

    if (
      resumeScreeningConfig.id !== newId ||
      resumeScreeningConfig.title !== newTitle ||
      resumeScreeningConfig.nodeOnly !== newNodeOnly
    ) {
      setResumeScreeningConfig({
        id: newId,
        title: newTitle,
        nodeOnly: newNodeOnly,
      });
    }
  }, [detailTarget?.name]);

  useEffect(() => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        config: {
          ...((detailTarget as OnboardingFlowStepNode).config ?? {}),
          type: OnboardingStepType.ResumeScreen,
          resumeScreeningConfigId: resumeScreeningConfig.id,
          resumeScreeningConfigTitle: resumeScreeningConfig.title,
          resumeScreeningConfigNodeOnly: resumeScreeningConfig.nodeOnly,
        },
      });

      if (!_.isEqual(newDetailTarget, detailTargetRef.current)) {
        detailTargetRef.current = newDetailTarget;
        setDetailTarget(newDetailTarget);
      }
    }
  }, [
    resumeScreeningConfig,
    currentFlow,
    editDraftEntityValue,
    setDetailTarget,
    stepType,
    detailTarget,
  ]);

  return (
    <PanelEntry title={`Resume Screening Config${required ? '*' : ``}`}>
      <Typography.Paragraph3 className="ml-1 py-1">
        Job post + node resume screen: If users signed up via a job post with a resume screen, they
        will be screened accordingly. Otherwise, they will be screened by this node's screening. If
        neither set, users will automatically pass.
      </Typography.Paragraph3>
      <Typography.Paragraph3 className="ml-1 py-1">
        Node only resume screen: All users will be screened by this node's screening. If it is not
        set, users will automatically pass.
      </Typography.Paragraph3>
      {!!resumeScreeningConfig.id && (
        <div className="flex gap-1 items-center">
          <Typography.Paragraph3 className="ml-1">
            Resume Screening Config ID: {resumeScreeningConfig.id}
          </Typography.Paragraph3>
          <CopyToClipboard text={resumeScreeningConfig.id} onCopy={notifyCopySuccess}>
            <Icon icon="copy" variant="neutral" size="sm" />
          </CopyToClipboard>
        </div>
      )}
      <ResumeScreeningConfigAutocomplete
        selectedResumeScreeningConfig={resumeScreeningConfig}
        setSelectedResumeScreeningConfig={setResumeScreeningConfig}
        disabled={uneditable}
      />
      {!!resumeScreeningConfig.id && (
        <div className="py-1 px-2 flex gap-1 items-center justify-between rounded-md border border-neutral-300 w-1/2">
          <Typography.Subtitle3>
            Mode: {resumeScreeningConfig.nodeOnly ? 'Node Only' : 'Job Post + Node'}
          </Typography.Subtitle3>
          <Switch
            checked={resumeScreeningConfig.nodeOnly}
            onCheckedChange={newNodeOnly => {
              setResumeScreeningConfig({
                ...resumeScreeningConfig,
                nodeOnly: newNodeOnly,
              });
            }}
          />
        </div>
      )}
    </PanelEntry>
  );
};

function conditionValueDefaults(type: ConditionValueType): ConditionValue {
  switch (type) {
    case ConditionValueType.String:
      return { type: ConditionValueType.String, value: '' };
    case ConditionValueType.Number:
      return { type: ConditionValueType.Number, value: 0 };
    case ConditionValueType.Boolean:
      return { type: ConditionValueType.Boolean, value: false };
    case ConditionValueType.CountryCodes:
      return { type: ConditionValueType.CountryCodes, value: [] };
    case ConditionValueType.USStateCodes:
      return { type: ConditionValueType.USStateCodes, value: [] };
    case ConditionValueType.Tag:
      return { type: ConditionValueType.Tag, value: [] };
    case ConditionValueType.SignupFlow:
      return { type: ConditionValueType.SignupFlow, value: '' };
    case ConditionValueType.ResumeScreenResult:
      return { type: ConditionValueType.ResumeScreenResult, value: ResumeScreeningResult.Pass };
    case ConditionValueType.WorkerSource:
      return { type: ConditionValueType.WorkerSource, value: WorkerSource.ScaleAPI };
  }
}

export const StepTypeSelector: React.FC<{ required?: boolean }> = ({ required }) => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const { draftFlows, editDraftEntityValue } = useDraftFlowStore(state => ({
    draftFlows: state.draftFlows,
    editDraftEntityValue: state.editDraftEntityValue,
  }));
  const uneditable =
    currentVersion !== -1 ||
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null);
  const [loadBalancerModalOpen, setLoadBalancerModalOpen] = useState<boolean>(false);

  const flow = currentFlow?._id ? draftFlows[currentFlow._id] ?? currentFlow : currentFlow;

  const hasOutgoingEdges = flow?.edges.some(
    edge => edge.sourceNode === (detailTarget as OnboardingFlowNode)?.name,
  );

  const handleLoadBalancerTypeChange = () => {
    if (!currentFlow || !detailTarget) return;
    const possibleTransitions =
      flow?.edges.filter(edge => edge.sourceNode === (detailTarget as OnboardingFlowNode)?.name) ??
      [];
    const loadBalancerEdges = possibleTransitions.filter(
      edge => edge.conditions[0].type === ConditionType.LoadBalancer,
    );
    loadBalancerEdges.forEach(edge => {
      const updatedEdge = {
        ...edge,
        conditions: [],
      };
      editDraftEntityValue(currentFlow._id, edge, updatedEdge);
    });
  };

  const handleEdit = (stepType: OnboardingStepType | OnboardingFlowNodeType) => {
    if (currentFlow && detailTarget) {
      // Remove load balancer transitions if the new step is not a load balancer
      if (
        (detailTarget as OnboardingFlowStepNode)?.stepType === OnboardingStepType.LoadBalancer &&
        stepType !== OnboardingStepType.LoadBalancer
      ) {
        handleLoadBalancerTypeChange();
      }

      if (stepType === OnboardingFlowNodeType.Terminus) {
        const newNode = {
          ..._.pick(detailTarget, TERMINUS_FIELDS),
          type: OnboardingFlowNodeType.Terminus,
          destinationTeamId: '',
          success: true,
        };
        const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, newNode);
        setDetailTarget(newDetailTarget);
      } else if (stepType === OnboardingStepType.LoadBalancer && hasOutgoingEdges) {
        setLoadBalancerModalOpen(true);
      } else {
        const newNode = {
          ..._.pick(detailTarget, STEP_FIELDS),
          type: OnboardingFlowNodeType.Step,
          stepType,
        };
        const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, newNode);
        setDetailTarget(newDetailTarget);
      }
    }
  };
  const editableNodeMap = getOnboardingStepAttributeMap(OnboardingStepAttributeKey.Editable);
  const onboardingFlowTypeMap = getOnboardingStepAttributeMap(
    OnboardingStepAttributeKey.OnboardingFlowType,
  );

  return (
    <PanelEntry title={`Step Type${required ? '*' : ``}`}>
      <SelectMenu
        options={[
          ...Object.values(OnboardingStepType)
            .filter(s => !DEPRECATED_OCP_STEPS.includes(s))
            .filter(s => editableNodeMap[s] || (canFreeEdit && currentFlow?.freeEdit))
            .filter(s => onboardingFlowTypeMap[s] === OnboardingFlowType.Growth),
          OnboardingFlowNodeType.Terminus,
        ].map(type => ({
          id: type,
          name: type,
        }))}
        selectedOptionId={
          (detailTarget as OnboardingFlowStepNode)?.stepType
            ? (detailTarget as OnboardingFlowStepNode).stepType
            : (detailTarget as OnboardingFlowNode)?.type === OnboardingFlowNodeType.Terminus
            ? OnboardingFlowNodeType.Terminus
            : OnboardingStepType.Dummy
        }
        setSelected={handleEdit}
        disabled={uneditable}
      />
      <LoadBalancerModal
        isOpen={loadBalancerModalOpen}
        onClose={() => setLoadBalancerModalOpen(false)}
        sourceNodeName={(detailTarget as OnboardingFlowNode)?.name}
      />
    </PanelEntry>
  );
};

export const SourceNodeSelector: React.FC<{ required?: boolean }> = ({ required }) => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const { draftFlows, editDraftEntityValue } = useDraftFlowStore(state => ({
    draftFlows: state.draftFlows,
    editDraftEntityValue: state.editDraftEntityValue,
  }));
  const uneditable =
    isTransitionUneditable(
      detailTarget as OnboardingFlowTransition,
      canFreeEdit ? currentFlow : null,
    ) || currentVersion !== -1;

  const handleEdit = (nodeName: string) => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        sourceNode: nodeName,
      });
      setDetailTarget(newDetailTarget);
    }
  };
  if (!currentFlow) return null;
  return (
    <PanelEntry title={`Source Node${required ? '*' : ``}`}>
      <SelectMenu
        options={[...(draftFlows[currentFlow._id]?.nodes ?? [])]
          .filter(
            node =>
              node.name !== (detailTarget as OnboardingFlowTransition)?.destNode &&
              !UNCONNECTABLE_STEP_TYPES_NAMES.includes(node.name),
          )
          .map(node => ({
            id: node.name,
            name: node.name,
          }))}
        selectedOptionId={(detailTarget as OnboardingFlowTransition)?.sourceNode}
        setSelected={handleEdit}
        disabled={uneditable}
      />
    </PanelEntry>
  );
};

export const DestNodeSelector: React.FC<{ required?: boolean }> = ({ required }) => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const { draftFlows, editDraftEntityValue } = useDraftFlowStore(state => ({
    draftFlows: state.draftFlows,
    editDraftEntityValue: state.editDraftEntityValue,
  }));
  const uneditable =
    isTransitionUneditable(
      detailTarget as OnboardingFlowTransition,
      canFreeEdit ? currentFlow : null,
    ) || currentVersion !== -1;

  const handleEdit = (nodeName: string) => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        destNode: nodeName,
      });
      setDetailTarget(newDetailTarget);
    }
  };
  if (!currentFlow) return null;
  return (
    <PanelEntry title={`Destination Node${required ? '*' : ``}`}>
      <SelectMenu
        options={[...(draftFlows[currentFlow._id]?.nodes ?? [])]
          .filter(
            node =>
              node.name !== (detailTarget as OnboardingFlowTransition)?.sourceNode &&
              !UNCONNECTABLE_STEP_TYPES_NAMES.includes(node.name),
          )
          .map(node => ({
            id: node.name,
            name: node.name,
          }))}
        selectedOptionId={(detailTarget as OnboardingFlowTransition)?.destNode}
        setSelected={handleEdit}
        disabled={uneditable}
      />
    </PanelEntry>
  );
};

export const ConditionsConfigurator: React.FC<{ required?: boolean }> = ({ required }) => {
  const { currentFlow, currentVersion, detailTarget, canFreeEdit } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    currentVersion: state.currentVersion,
    detailTarget: state.detailTarget as OnboardingFlowTransition,
    canFreeEdit: state.canFreeEdit,
  }));
  const { editDraftEntityValue, draftFlows } = useDraftFlowStore(state => ({
    editDraftEntityValue: state.editDraftEntityValue,
    draftFlows: state.draftFlows,
  }));
  const uneditable =
    (detailTarget.name !== 'Resume Screen -> Skill Selection' &&
      isTransitionUneditable(
        detailTarget as OnboardingFlowTransition,
        canFreeEdit ? currentFlow : null,
      )) ||
    currentVersion !== -1;
  const isLoadBalancerEdge =
    detailTarget?.conditions && detailTarget?.conditions[0]?.type === ConditionType.LoadBalancer;
  const [sourceStepConfigId, setSourceStepConfigId] = useState<string>('');
  const [sourceStepType, setSourceStepType] = useState<OnboardingStepType>();

  const handleEditType = (type: ConditionType, conditionIndex: number) => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget };
      switch (type) {
        case ConditionType.UserProperty:
          newDetailTarget.conditions[conditionIndex] = {
            type: ConditionType.UserProperty,
            key: 'utmSource',
            value: { type: ConditionValueType.String, value: '' },
          };
          break;
        case ConditionType.StepOutput: {
          const sourceNode = (detailTarget as OnboardingFlowTransition).sourceNode;
          const sourceStep = draftFlows[currentFlow._id].nodes.find(
            node => node.name === sourceNode,
          ) as OnboardingFlowStepNode;
          const sourceStepType = sourceStep.stepType;
          const sourceStepOutputType = getOnboardingStepAttributeMap(
            OnboardingStepAttributeKey.OutputType,
          )[sourceStepType];
          newDetailTarget.conditions[conditionIndex] = {
            type: ConditionType.StepOutput,
            value:
              sourceStepOutputType === ConditionValueType.String
                ? { type: ConditionValueType.String, value: '' }
                : sourceStepOutputType === ConditionValueType.ResumeScreenResult
                ? {
                    type: ConditionValueType.ResumeScreenResult,
                    value: ResumeScreeningResult.Pass,
                  }
                : { type: ConditionValueType.Boolean, value: true },
          };
          break;
        }
        case ConditionType.CatchAll:
          newDetailTarget.conditions[conditionIndex] = {
            type: ConditionType.CatchAll,
          };
          break;
        case ConditionType.WorkerSkill: {
          // TODO: we shouldn't allow blank workerSkill id to be saved
          // We currently also allow this to happen in GrantsConfigurator
          const emptyWorkerSkillCondition: WorkerSkillCondition = {
            type: ConditionType.WorkerSkill,
            id: '',
            dimension: WorkerSkillDimension.Overall,
            category: WorkerSkillDimensionQualityCategory.Unranked,
          };
          newDetailTarget.conditions[conditionIndex] = emptyWorkerSkillCondition;
          break;
        }
        case ConditionType.ResumeMetadata: {
          newDetailTarget.conditions[conditionIndex] = {
            type: ConditionType.ResumeMetadata,
            field: ResumeMetadataFields.School,
            values: [],
          };
          break;
        }
        case ConditionType.UTM: {
          newDetailTarget.conditions[conditionIndex] = {
            type: ConditionType.UTM,
            field: UTMParameters.UTM_SOURCE,
            values: [],
          };
          break;
        }
      }
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
      maybeSetSourceStep();
    }
  };

  const maybeSetSourceStep = () => {
    const sourceNode = (detailTarget as OnboardingFlowTransition).sourceNode;
    if (!currentFlow) return;
    const sourceStep = draftFlows[currentFlow._id].nodes.find(
      node => node.name === sourceNode,
    ) as OnboardingFlowStepNode;
    const sourceStepType = sourceStep.stepType;
    if (
      sourceStepType === OnboardingStepType.InterviewOutcome ||
      sourceStepType === OnboardingStepType.AsyncInterview
    ) {
      const interviewId = (
        sourceStep.config as InterviewOutcomeStepConfig | AsyncInterviewStepConfig
      )?.interviewId;
      if (interviewId) {
        setSourceStepConfigId(interviewId);
      }
    } else if (sourceStepType === OnboardingStepType.SkillScreening) {
      const screeningId = (sourceStep.config as SkillScreeningStepConfig)?.screeningConfigId;
      if (screeningId) {
        setSourceStepConfigId(screeningId);
      }
    }
    setSourceStepType(sourceStepType);
  };

  const handleEditUserPropertyKey = (prop: ConditionUserProperty, conditionIndex: number) => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget };

      newDetailTarget.conditions[conditionIndex] = {
        ...(newDetailTarget.conditions[conditionIndex] as UserPropertyCondition),
        key: prop,
        value: conditionValueDefaults(CONDITION_USER_PROPERTIES.find(p => p.prop === prop)!.type),
      };
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
    }
  };

  const handleEditConditionValue = (val: ConditionValue, conditionIndex: number) => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget };
      newDetailTarget.conditions[conditionIndex] = {
        ...(newDetailTarget.conditions[conditionIndex] as
          | UserPropertyCondition
          | StepOutputCondition),
        value: val,
      };
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
    }
  };

  const handleEditWorkerSkillConditionValue = (
    val: WorkerSkillConditionValue,
    conditionIndex: number,
  ) => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget };
      newDetailTarget.conditions[conditionIndex] = {
        ...(newDetailTarget.conditions[conditionIndex] as WorkerSkillCondition),
        id: val.id,
        dimension: val.dimension!,
        category: val.category!,
        includeInherited: val.includeInherited,
      };
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
    }
  };

  const handleEditResumeMetadataConditionValue = (
    val: ResumeMetadataCondition,
    conditionIndex: number,
  ) => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget };
      newDetailTarget.conditions[conditionIndex] = val;
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
    }
  };

  const handleEditUTMConditionValue = (val: UTMCondition, conditionIndex: number) => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget };
      newDetailTarget.conditions[conditionIndex] = val;
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
    }
  };

  const deleteCondition = (conditionIndex: number) => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget };
      newDetailTarget.conditions.splice(conditionIndex, 1);
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
    }
  };
  const addCondition = () => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = { ...detailTarget };
      newDetailTarget.conditions.push({
        type: ConditionType.UserProperty,
        key: ConditionValueType.Tag,
        value: conditionValueDefaults(ConditionValueType.Tag),
      });
      editDraftEntityValue(currentFlow._id, detailTarget, newDetailTarget);
    }
  };

  const defaultFallback = isDefaultFallback(detailTarget as OnboardingFlowTransition);

  const defaultFallbackCheckboxHandler = () => {
    const newDetailTarget = { ...detailTarget };
    newDetailTarget.conditions.splice(0, newDetailTarget.conditions.length);
    if (!defaultFallback) {
      newDetailTarget.conditions.push({ type: ConditionType.CatchAll });
    } else {
      newDetailTarget.conditions.push({
        type: ConditionType.UserProperty,
        key: ConditionValueType.Tag,
        value: conditionValueDefaults(ConditionValueType.Tag),
      });
    }
    editDraftEntityValue(currentFlow?._id ?? '', detailTarget, newDetailTarget);
  };

  if (!detailTarget) return null;
  if (isLoadBalancerEdge) {
    return (
      <PanelEntry title={`Conditions${required ? '*' : ``}`}>
        <div className="flex flex-col gap-2 w-full items-center">
          <Typography.Caption>
            To edit conditions, edit inside source node: {detailTarget.sourceNode}
          </Typography.Caption>
        </div>
      </PanelEntry>
    );
  }
  return (
    <PanelEntry title={`Conditions${required ? '*' : ``}`}>
      <div className="flex flex-row gap-2 mb-2">
        <BaseCheckbox
          checked={defaultFallback}
          className=""
          onChange={defaultFallbackCheckboxHandler}
          size="md"
          variant="primary"
          disabled={uneditable}
        />
        <InputLabel
          className="text-gray-500"
          labelDescription="If contributors do not satisfy any other transition's conditions (or if this is the only transition), they will default and exit through this transition. This prevents the user from getting stuck on this step."
          size="md"
          tooltipContentClassName="text-gray-500"
        >
          Make this a Default Fallback transition
        </InputLabel>
      </div>
      {!defaultFallback &&
        detailTarget.conditions.map((condition: TransitionCondition, i: number) => (
          <div key={i} className="flex gap-2 items-center">
            {i === 0 ? (
              <Typography.Paragraph1>If</Typography.Paragraph1>
            ) : (
              <Typography.Paragraph1>and</Typography.Paragraph1>
            )}
            <SelectMenu
              options={Object.values(ConditionType)
                .filter(type => type !== ConditionType.CatchAll)
                .map(type => ({
                  id: type,
                  name: type,
                }))}
              selectedOptionId={condition.type}
              setSelected={(val: ConditionType) => handleEditType(val, i)}
              disabled={uneditable}
            />
            {condition.type === ConditionType.UserProperty && (
              <>
                <SelectMenu
                  options={CONDITION_USER_PROPERTIES.map(p => ({ id: p.prop, name: p.prop }))}
                  selectedOptionId={condition.key}
                  setSelected={(val: ConditionUserProperty) => handleEditUserPropertyKey(val, i)}
                />
                {condition.value.type === ConditionValueType.String ||
                condition.value.type === ConditionValueType.CountryCodes ||
                condition.value.type === ConditionValueType.USStateCodes ||
                condition.value.type === ConditionValueType.SignupFlow ||
                condition.value.type === ConditionValueType.WorkerSource ? (
                  <>
                    <Typography.Paragraph1>is</Typography.Paragraph1>
                    <ConditionValueInput
                      id={`condition-value-${i}`}
                      value={condition.value}
                      onChange={val => handleEditConditionValue(val, i)}
                    />
                  </>
                ) : condition.value.type === ConditionValueType.Tag ? (
                  <>
                    <Typography.Paragraph1>has one of</Typography.Paragraph1>
                    <ConditionValueInput
                      id={`condition-value-${i}`}
                      value={condition.value}
                      onChange={val => handleEditConditionValue(val, i)}
                    />
                  </>
                ) : condition.value.type === ConditionValueType.Boolean ? (
                  <>
                    <Typography.Paragraph1>is</Typography.Paragraph1>
                    <SelectMenu
                      options={[
                        { id: 'true', name: 'true' },
                        { id: 'false', name: 'false' },
                      ]}
                      selectedOptionId={condition.value.value.toString()}
                      setSelected={(val: string) =>
                        handleEditConditionValue(
                          { type: ConditionValueType.Boolean, value: val === 'true' },
                          i,
                        )
                      }
                    />
                  </>
                ) : null}
              </>
            )}
            {condition.type === ConditionType.StepOutput && (
              <ConditionValueInput
                id={`condition-value-${i}`}
                value={condition.value}
                onChange={val => handleEditConditionValue(val, i)}
                disabled={uneditable}
                sourceStepConfigId={sourceStepConfigId}
                sourceStepType={sourceStepType}
              />
            )}
            {condition.type === ConditionType.WorkerSkill && (
              <WorkerSkillConditionValueInput
                id={`condition-value-${i}`}
                value={{
                  id: condition.id,
                  dimension: condition.dimension,
                  category: condition.category,
                  includeInherited: condition.includeInherited,
                }}
                onChange={val => handleEditWorkerSkillConditionValue(val, i)}
                disabled={uneditable}
              />
            )}
            {condition.type === ConditionType.ResumeMetadata && (
              <ResumeMetadataConditionValueInput
                value={condition}
                onChange={val => handleEditResumeMetadataConditionValue(val, i)}
                disabled={uneditable}
              />
            )}
            {condition.type === ConditionType.UTM && (
              <UTMConditionValueInput
                value={condition}
                onChange={val => handleEditUTMConditionValue(val, i)}
                disabled={uneditable}
              />
            )}
            <div className="ml-auto">
              <Icon
                rounded
                size="sm"
                icon="trash"
                variant="neutral"
                onClick={() => deleteCondition(i)}
              />
              {/* // if implementing tags try TagsAutocomplete */}
            </div>
          </div>
        ))}
      {!defaultFallback && !uneditable && (
        <Button variant="transparent" className="w-fit ml-2" onClick={addCondition}>
          + Add Condition
        </Button>
      )}
    </PanelEntry>
  );
};

export const BypassWhitelistConfigurator: React.FC<{
  forceEditable?: boolean;
  restrictToTestAccounts?: boolean;
}> = ({ forceEditable = false, restrictToTestAccounts = false }) => {
  const [emailString, setEmailString] = useState<string>('');
  const { currentFlow, currentVersion, detailTarget, canFreeEdit } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    currentVersion: state.currentVersion,
    detailTarget: state.detailTarget as OnboardingFlowNode,
    canFreeEdit: state.canFreeEdit,
  }));
  const uneditable =
    currentVersion !== -1 ||
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null);
  const [message, setMessage] = useState('');

  const { addEmailByStepName, removeEmails, whitelist, publishBypassWhitelistForStep } =
    useBypassWhitelistStore(state => ({
      addEmailByStepName: state.addEmailByStepName,
      removeEmails: state.removeEmails,
      whitelist: state.whitelist,
      publishBypassWhitelistForStep: state.publishBypassWhitelistForStep,
    }));

  const convertEmailStringToEmails = () => {
    if (emailString === '') {
      setMessage('Email cannot be empty');
      return;
    }
    const emails = emailString.split(',').map(e => e.trim());
    emails.forEach(email => {
      const wasAdded = addEmailByStepName(detailTarget.name, email);
      if (!wasAdded) {
        setMessage(`Email ${email} already exists`);
      } else {
        setMessage('');
      }
    });
    setEmailString('');
  };

  const handleRemoveEmail = (email: string) => {
    removeEmails(detailTarget.name, [email]);
  };

  return (
    <PanelEntry title="Bypass Whitelist">
      <Typography.Caption>
        To update the bypass whitelist, add and remove emails below and then save. There is no need
        to publish a new flow to update the whitelist.
      </Typography.Caption>
      {restrictToTestAccounts && (
        <Typography.Caption>
          Only test accounts will be able to bypass this step.
        </Typography.Caption>
      )}
      {uneditable && <Typography.Caption>Switch to draft mode to edit</Typography.Caption>}
      <div className="flex flex-col gap-2 w-full items-center">
        <TextArea
          className="w-full grow"
          rows={4}
          resizable={true}
          id="whitelist-emails"
          placeholder="Comma-separated list of emails"
          value={emailString}
          onChange={e => setEmailString(e.target.value)}
          disabled={!forceEditable && uneditable}
        />
        <div className="flex w-full">
          <div className="flex ml-auto gap-2">
            <Button
              variant="primary"
              onClick={convertEmailStringToEmails}
              disabled={!forceEditable && uneditable}
            >
              Add
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                try {
                  await publishBypassWhitelistForStep(detailTarget.name, restrictToTestAccounts);
                  setMessage('');
                } catch (e) {
                  setMessage(`Error: ${e.message}`);
                }
              }}
              disabled={!forceEditable && uneditable}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
      <Typography.Paragraph2>{message}</Typography.Paragraph2>
      {Array.from(whitelist[detailTarget.name] || new Set()).map(email => (
        <div
          key={email}
          className="flex justify-between items-center px-2 py-1 border-b border-neutral-200"
        >
          <Typography.Paragraph2>{email}</Typography.Paragraph2>
          <Icon
            rounded
            size="sm"
            variant="neutral"
            icon="times"
            onClick={() => handleRemoveEmail(email)}
            disabled={!forceEditable && uneditable}
          />
        </div>
      ))}
    </PanelEntry>
  );
};
export const ConnectedNodes: React.FC = () => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const { draftFlows, addNode, addSourceNode, addTransition } = useDraftFlowStore(state => ({
    draftFlows: state.draftFlows,
    addNode: state.addNode,
    addSourceNode: state.addSourceNode,
    addTransition: state.addTransition,
  }));
  const uneditable =
    currentVersion !== -1 ||
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null);
  const flow = draftFlows[currentFlow?._id ?? ''] ?? currentFlow;
  const currentNodeName = (detailTarget as OnboardingFlowNode)?.name;
  const transitions = flow?.edges.filter(
    (transition: OnboardingFlowTransition) =>
      transition.sourceNode === currentNodeName || transition.destNode === currentNodeName,
  );
  const sourceSteps: string[] = [
    ...new Set(
      transitions
        .filter((transition: OnboardingFlowTransition) => transition.destNode === currentNodeName)
        .map((transition: OnboardingFlowTransition) => transition.sourceNode),
    ),
  ] as string[];
  const destSteps: string[] = [
    ...new Set(
      transitions
        .filter((transition: OnboardingFlowTransition) => transition.sourceNode === currentNodeName)
        .map((transition: OnboardingFlowTransition) => transition.destNode),
    ),
  ] as string[];
  const handleStepClick = (stepName: string) => {
    const newTarget = flow.nodes.find((node: OnboardingFlowNode) => node.name === stepName);
    if (newTarget) setDetailTarget(newTarget);
  };
  const handleTransitionClick = (transition: OnboardingFlowTransition) =>
    setDetailTarget(transition);

  const handleAddSourceNode = () => {
    if (currentFlow && detailTarget)
      addSourceNode(currentFlow?._id, detailTarget as OnboardingFlowNode);
  };

  const handleAddDestNode = () => {
    if (currentFlow && detailTarget) addNode(currentFlow?._id, detailTarget as OnboardingFlowNode);
  };

  const handleAddSourceTransition = (sourceNode: string) => {
    if (currentFlow && detailTarget) addTransition(currentFlow?._id, sourceNode, detailTarget.name);
  };

  const handleAddDestinationTransition = (destNode: string) => {
    if (currentFlow && detailTarget) addTransition(currentFlow?._id, detailTarget.name, destNode);
  };

  return (
    <>
      {!!sourceSteps?.length && (
        <PanelEntry
          title="Source Step(s)"
          titleButton={
            !uneditable ? (
              <Icon rounded icon="plus" variant="neutral" onClick={handleAddSourceNode} />
            ) : undefined
          }
        >
          {sourceSteps.map((sourceStep: string) => (
            <div key={sourceStep}>
              <div className="flex items-center">
                <div onClick={() => handleStepClick(sourceStep)}>
                  <Typography.Paragraph2
                    color="text-primary-600"
                    className="w-fit cursor-pointer underline"
                  >
                    {sourceStep}
                  </Typography.Paragraph2>
                </div>
                {!uneditable && (
                  <Icon
                    className="ml-auto mr-1"
                    rounded
                    icon="plus"
                    variant="neutral"
                    size="sm"
                    onClick={() => handleAddSourceTransition(sourceStep)}
                  />
                )}
              </div>
              <div className="flex gap-1">
                <Typography.Paragraph3>via</Typography.Paragraph3>
                <div className="flex flex-col">
                  {transitions
                    .filter((t: OnboardingFlowTransition) => t.sourceNode === sourceStep)
                    .map((t: OnboardingFlowTransition) => (
                      <div key={t.name} onClick={() => handleTransitionClick(t)}>
                        <Typography.Paragraph3 className="w-fit cursor-pointer underline">
                          {t.name}
                        </Typography.Paragraph3>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ))}
        </PanelEntry>
      )}
      {
        <PanelEntry
          title="Destination Step(s)"
          titleButton={
            !uneditable ? (
              <Icon rounded icon="plus" variant="neutral" onClick={handleAddDestNode} />
            ) : undefined
          }
        >
          {destSteps.map((destStep: string) => (
            <div key={destStep}>
              <div className="flex items-center">
                <div onClick={() => handleStepClick(destStep)}>
                  <Typography.Paragraph2
                    color="text-primary-600"
                    className="w-fit cursor-pointer underline"
                  >
                    {destStep}
                  </Typography.Paragraph2>
                </div>

                {!uneditable && (
                  <Icon
                    className="ml-auto mr-1"
                    rounded
                    icon="plus"
                    variant="neutral"
                    size="sm"
                    onClick={() => handleAddDestinationTransition(destStep)}
                  />
                )}
              </div>
              <div className="flex gap-1">
                <Typography.Paragraph3>via</Typography.Paragraph3>
                <div className="flex flex-col">
                  {transitions
                    .filter((t: OnboardingFlowTransition) => t.destNode === destStep)
                    .map((t: OnboardingFlowTransition) => (
                      <div key={t.name} onClick={() => handleTransitionClick(t)}>
                        <Typography.Paragraph3 className="w-fit cursor-pointer underline">
                          {t.name}
                        </Typography.Paragraph3>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          ))}
          <div className="pt-2">
            <DisableDefaultFallbackValidationToggle />
          </div>
        </PanelEntry>
      }
    </>
  );
};

const SuccessfulTerminusNodeToggle: React.FC<{
  required?: boolean;
  routeToFlow: boolean;
}> = ({ required, routeToFlow }) => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const editDraftEntityValue = useDraftFlowStore(state => state.editDraftEntityValue);
  const uneditable =
    currentVersion !== -1 ||
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null);

  const toggleSuccess = () => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        success: !(detailTarget as OnboardingFlowTerminusNode).success,
      });
      setDetailTarget(newDetailTarget);
    }
  };

  const toggleProdWT = () => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        prodWorkerTeam: !(detailTarget as OnboardingFlowTerminusNode)?.prodWorkerTeam ?? false,
      });
      setDetailTarget(newDetailTarget);
    }
  };

  return (
    <PanelEntry title={`Successful Terminus Node${required ? '*' : ``}`}>
      <Switch
        label="Successful Terminus Node"
        labelPlacement="right"
        checked={(detailTarget as OnboardingFlowTerminusNode).success}
        onCheckedChange={toggleSuccess}
        disabled={uneditable}
      />
      {(detailTarget as OnboardingFlowTerminusNode).success && !routeToFlow && (
        <div className="flex items-center gap-2">
          <BaseCheckbox
            checked={(detailTarget as OnboardingFlowTerminusNode)?.prodWorkerTeam ?? false}
            className=""
            onClick={() => toggleProdWT()}
            size="md"
            variant="primary"
          />
          <InputLabel
            className="text-gray-500"
            labelDescription="Non-production refers to any team that is not for a production project, such as a capacity building or testing team"
            size="md"
            tooltipContentClassName="text-gray-500"
          >
            Routes to Production Worker Team
          </InputLabel>
        </div>
      )}
    </PanelEntry>
  );
};

const DisableDefaultFallbackValidationToggle: React.FC<{}> = () => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const editDraftEntityValue = useDraftFlowStore(state => state.editDraftEntityValue);
  const uneditable =
    currentVersion !== -1 ||
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null);

  const toggleDisableDefaultFallbackValidation = () => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        disableDefaultFallbackValidation: !(detailTarget as OnboardingFlowStepNode)
          .disableDefaultFallbackValidation,
      });
      setDetailTarget(newDetailTarget);
    }
  };

  return (
    <Switch
      label="Bypass Default Fallback Transition Validation"
      helperText="Enable to not require a default fallback transition for the source step, holding contributors at a step until non-fallback conditions (e.g. presence of a tag) are met."
      labelPlacement="right"
      checked={(detailTarget as OnboardingFlowStepNode).disableDefaultFallbackValidation}
      onCheckedChange={toggleDisableDefaultFallbackValidation}
      disabled={uneditable}
    />
  );
};

export const SelectCommunityInput: React.FC = () => {
  const { currentFlow, detailTarget, setDetailTarget } = useOCPStore(state => ({
    currentFlow: state.currentFlow,
    currentVersion: state.currentVersion,
    detailTarget: state.detailTarget,
    setDetailTarget: state.setDetailTarget,
  }));
  const editDraftEntityValue = useDraftFlowStore(state => state.editDraftEntityValue);
  const [community, setCommunity] = useState<CommunityType>(
    ((detailTarget as OnboardingFlowStepNode).config as JoinCommunityStepConfig)?.communityType ??
      CommunityType.Discourse,
  );

  const handleEdit = useCallback(() => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        config: {
          ...((detailTarget as OnboardingFlowStepNode).config ?? {}),
          communityType: community,
        },
      });
      setDetailTarget(newDetailTarget);
    }
  }, [currentFlow, detailTarget, editDraftEntityValue, setDetailTarget, community]);

  return (
    <PanelEntry title={`Select Community*`}>
      <SelectMenu
        className="w-48"
        label=""
        menuDirection="bottom"
        onBlur={handleEdit}
        options={[
          { id: CommunityType.Discourse, name: 'Discourse' },
          { id: CommunityType.Slack, name: 'Slack' },
        ]}
        selectedOptionId={community}
        setSelected={v => setCommunity(v)}
        variant="minimal"
        required={true}
      />
    </PanelEntry>
  );
};
